import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, CheckCircle2, Brain, Target, Lightbulb, TrendingUp, Users, Sparkles, BarChart3, Compass, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxjss3TgDQN6ihmsA6XV324S4E1Qh-L3Gn0sa5wCh8JWmOo15dZjogIMINaX5rVwLsN/exec';
// --- Types ---
type Option = {
  text: string;
  score: number;
};
type Question = {
  id: number;
  text: string;
  options: Option[];
};
type LevelData = {
  level: number;
  title: string;
  subtitle: string;
  sections: {
    type: 'text' | 'block';
    title: string;
    icon?: React.ElementType;
    items: string[];
  }[];
};
// --- Data ---
const questions: Question[] = [{
  id: 1,
  text: 'Як сьогодні працює залучення клієнтів у Вашому бізнесі?',
  options: [{
    text: 'Кожного разу по-різному: рекомендації, випадкові запуски, особисті контакт',
    score: 1
  }, {
    text: 'Є канали, але результат нестабільний',
    score: 2
  }, {
    text: 'Є воронки, але вони залежать від конкретних людей',
    score: 3
  }, {
    text: 'Є система й метрики, я розумію, що відбувається',
    score: 4
  }, {
    text: 'Потік клієнтів керований і масштабується без моєї участі',
    score: 5
  }]
}, {
  id: 2,
  text: 'Хто приймає ключові маркетингові рішення?',
  options: [{
    text: 'Я самостійно, інтуїтивно',
    score: 1
  }, {
    text: 'Я, але з опорою на підрядників',
    score: 2
  }, {
    text: 'Команда, але фінальне слово за мною',
    score: 3
  }, {
    text: 'Є відповідальний керівник',
    score: 4
  }, {
    text: 'Я задаю стратегію, система працює автономно',
    score: 5
  }]
}, {
  id: 3,
  text: 'Що відбувається з бізнесом, якщо Ви випадаєте на тиждень?',
  options: [{
    text: 'Починається хаос',
    score: 1
  }, {
    text: 'Продажі одразу просідають',
    score: 2
  }, {
    text: 'Усе працює, але повільніше',
    score: 3
  }, {
    text: 'Ключові процеси зберігаються',
    score: 4
  }, {
    text: 'Бізнес майже не залежить від моєї присутності',
    score: 5
  }]
}, {
  id: 4,
  text: 'Як Ви приймаєте управлінські рішення?',
  options: [{
    text: 'За відчуттями та терміновістю',
    score: 1
  }, {
    text: 'Орієнтуюся на гроші «на рахунку»',
    score: 2
  }, {
    text: 'Є базова аналітика',
    score: 3
  }, {
    text: 'Є цифри, гіпотези та пріоритети',
    score: 4
  }, {
    text: 'Рішення базуються на прогнозах і сценаріях',
    score: 5
  }]
}, {
  id: 5,
  text: 'Як у Вас побудована unit-економіка?',
  options: [{
    text: 'Приблизно розумію маржинальність',
    score: 1
  }, {
    text: 'Рахую окремі проєкти',
    score: 2
  }, {
    text: 'Є модель, але використовую її рідко',
    score: 3
  }, {
    text: 'Unit-економіка впливає на рішення',
    score: 4
  }, {
    text: 'Це ключовий інструмент управління зростанням',
    score: 5
  }]
}, {
  id: 6,
  text: 'Як Ви працюєте з командою та підрядниками?',
  options: [{
    text: 'Постійно пояснюю та контролюю',
    score: 1
  }, {
    text: 'Є люди, але все тримається на мені',
    score: 2
  }, {
    text: 'Є процеси, але вони не завжди працюють',
    score: 3
  }, {
    text: 'Є відповідальність і KPI',
    score: 4
  }, {
    text: 'Команда самостійно досягає результатів',
    score: 5
  }]
}, {
  id: 7,
  text: 'Що сьогодні найбільше стримує зростання бізнесу?',
  options: [{
    text: 'Нестача часу й енергії',
    score: 1
  }, {
    text: 'Важко делегувати',
    score: 2
  }, {
    text: 'Багато дій, мало ефекту',
    score: 3
  }, {
    text: 'Команда не встигає за ростом',
    score: 4
  }, {
    text: 'Хочу більшого масштабу, але не бачу наступного кроку',
    score: 5
  }]
}, {
  id: 8,
  text: 'Як Ви працюєте з ростом і гіпотезами?',
  options: [{
    text: 'Реагую на можливості',
    score: 1
  }, {
    text: 'Тестую хаотично',
    score: 2
  }, {
    text: 'Є ідеї, але немає системи',
    score: 3
  }, {
    text: 'Є процес growth-експериментів',
    score: 4
  }, {
    text: 'Зростання кероване й повторюване',
    score: 5
  }]
}, {
  id: 9,
  text: 'Яку роль Ви зараз виконуєте в бізнесі?',
  options: [{
    text: 'Операційний виконавець',
    score: 1
  }, {
    text: 'Менеджер усього',
    score: 2
  }, {
    text: 'Контролер і координатор',
    score: 3
  }, {
    text: 'Лідер і стратег',
    score: 4
  }, {
    text: 'Архітектор систем і напрямів',
    score: 5
  }]
}, {
  id: 10,
  text: 'Як Ви відчуваєте свій поточний етап?',
  options: [{
    text: '«Білка в колесі»',
    score: 1
  }, {
    text: 'Є стабільність, але все тримається на мені',
    score: 2
  }, {
    text: 'Усе працює, але відчуваю стелю',
    score: 3
  }, {
    text: 'Хочу більшого масштабу та впливу',
    score: 4
  }, {
    text: 'Готовий(а) діяти на іншому рівні',
    score: 5
  }]
}];
const resultsData: Record<number, LevelData> = {
  1: {
    level: 1,
    title: 'Рівень 1. Хаос',
    subtitle: 'Підприємець у режимі виживання',
    sections: [{
      type: 'text',
      title: 'Як це виглядає',
      items: ['Берете будь-які можливості, аби не втратити дохід', 'Працюєте багато, але відчуття руху вперед немає', 'Рішення приймаються швидко і з напруги', 'Усе тримається на вас']
    }, {
      type: 'text',
      title: 'Як ви мислите',
      items: ['«Головне – не втратити клієнтів»', '«Зараз не до систем, треба заробляти»', '«Світ нестабільний, потрібно крутитись»']
    }, {
      type: 'text',
      title: 'Як ви приймаєте рішення',
      items: ['З емоцій', 'Зі страху', 'Реактивно']
    }, {
      type: 'block',
      title: 'Hard Skills',
      icon: Target,
      items: ['Зафіксувати один продукт і одну основну пропозицію', 'Побудувати базову воронку: контакт → інтерес → оплата', 'Почати рахувати базові цифри: джерело клієнта, витрати, дохід', 'Ввести просту фінансову модель']
    }, {
      type: 'block',
      title: 'Soft Skills',
      icon: Users,
      items: ['Зупинити хаотичні рішення', 'Взяти відповідальність замість пошуку винних', 'Навчитися говорити «ні»', 'Сповільнитись, щоб почати мислити']
    }]
  },
  2: {
    level: 2,
    title: 'Рівень 2. Реактивний контроль',
    subtitle: 'Підприємець, який тягне все на собі',
    sections: [{
      type: 'text',
      title: 'Як це виглядає',
      items: ['Є клієнти і стабільний дохід', 'Ви - головний операційний центр бізнесу', 'Без вас система зупиняється', 'Постійна зайнятість = ілюзія контролю']
    }, {
      type: 'text',
      title: 'Як ви мислите',
      items: ['«Якщо не я – то ніхто»', '«Я знаю краще»', '«Треба ще трохи допрацювати»']
    }, {
      type: 'text',
      title: 'Як ви приймаєте рішення',
      items: ['З досвіду', 'З інтуїції', 'Через власну участь у всьому']
    }, {
      type: 'block',
      title: 'Hard Skills',
      icon: Target,
      items: ["Зв'язати маркетинг, продажі та фінанси в одну систему", 'Побудувати Acquisition System', 'Порахувати unit-економіку', 'Стандартизувати повторювані процеси']
    }, {
      type: 'block',
      title: 'Soft Skills',
      icon: Users,
      items: ['Вийти з ролі «рятівника бізнесу»', 'Почати довіряти системі, а не контролювати людей', 'Відпускати дрібні рішення', 'Змінити фокус з «робити» на «керувати»']
    }]
  },
  3: {
    level: 3,
    title: 'Рівень 3. Системний підприємець',
    subtitle: 'Підприємець, який керує через логіку',
    sections: [{
      type: 'text',
      title: 'Як це виглядає',
      items: ['Бізнес працює стабільно', 'Є процеси, цифри, структура', 'Рішення стають спокійнішими', "З'являється передбачуваність"]
    }, {
      type: 'text',
      title: 'Як ви мислите',
      items: ['«Система важливіша за окрему дію»', '«Рішення = цифри + контекст»', '«Не все потрібно робити одразу»']
    }, {
      type: 'text',
      title: 'Як ви приймаєте рішення',
      items: ['Через метрики', 'Через аналітику', 'Через перевірені гіпотези']
    }, {
      type: 'block',
      title: 'Hard Skills',
      icon: Target,
      items: ['Побудувати Growth Acquisition + Retention', 'Працювати з гіпотезами масштабування', 'Впровадити управлінський dashboard', "Пов'язати продукт, ціну, канали і команду в єдину модель"]
    }, {
      type: 'block',
      title: 'Soft Skills',
      icon: Users,
      items: ['Вийти з операційного мислення', 'Навчитись бачити бізнес зверху', 'Терпіти невизначеність росту', 'Не плутати стабільність із розвитком']
    }]
  },
  4: {
    level: 4,
    title: 'Рівень 4. Стратег',
    subtitle: 'Підприємець, який формує напрям',
    sections: [{
      type: 'text',
      title: 'Як це виглядає',
      items: ['Ви працюєте з баченням, а не з задачами', 'Команда реалізує, ви – спрямовуєте', 'Бізнес росте не ривками, а логікою', 'Рішень менше, але вони вагоміші']
    }, {
      type: 'text',
      title: 'Як ви мислите',
      items: ['«Зростання – це архітектура»', '«Не швидше, а глибше»', '«Команда – частина системи»']
    }, {
      type: 'text',
      title: 'Як ви приймаєте рішення',
      items: ['Через стратегію', 'Через довгостроковий ефект', 'Через вплив на систему']
    }, {
      type: 'block',
      title: 'Hard Skills',
      icon: Target,
      items: ['Формувати довгострокову стратегію росту', 'Будувати управління через ролі та відповідальність', 'Передати операційні рішення', 'Працювати з масштабом, а не оптимізацією дрібниць']
    }, {
      type: 'block',
      title: 'Soft Skills',
      icon: Users,
      items: ['Відпустити контроль без втрати впливу', 'Довіряти людям', 'Тримати бачення в хаосі', 'Діяти зі спокою']
    }]
  },
  5: {
    level: 5,
    title: 'Рівень 5. Лідер',
    subtitle: 'Підприємець, який створює систему і сенс',
    sections: [{
      type: 'text',
      title: 'Як це виглядає',
      items: ['Бізнес працює без вашої постійної участі', 'Команда приймає рішення самостійно', 'Зростання – результат системи', 'Ви впливаєте ширше, ніж на власну компанію']
    }, {
      type: 'text',
      title: 'Як ви мислите',
      items: ['«Я створюю середовище»', '«Рішення мають жити довше за мене»', '«Масштаб – це не кількість, а якість»']
    }, {
      type: 'text',
      title: 'Як ви приймаєте рішення',
      items: ['З ясності', 'З бачення', 'З відповідальності за майбутнє']
    }, {
      type: 'text',
      title: 'Ключова зміна',
      items: ['Ви перестаєте бігти і починаєте вести']
    }]
  }
};
// Export open function for external use
let openSurveyExternal: (() => void) | null = null;
export function openSurvey() {
  if (openSurveyExternal) {
    openSurveyExternal();
  }
}
export function SurveyModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [calculatedLevel, setCalculatedLevel] = useState(1);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  const [formErrors, setFormErrors] = useState({
    phone: '',
    email: ''
  });
  const scrollPositionRef = useRef(0);
  // Expose open function
  useEffect(() => {
    openSurveyExternal = () => {
      scrollPositionRef.current = window.scrollY;
      setIsOpen(true);
      setShowIntro(true);
      setCurrentStep(0);
      setAnswers([]);
      setCalculatedLevel(1);
      setShowContactForm(false);
      setShowResults(false);
      setFormData({
        name: '',
        phone: '',
        email: '',
        company: ''
      });
      setFormErrors({
        phone: '',
        email: ''
      });
    };
    return () => {
      openSurveyExternal = null;
    };
  }, []);
  // Initialize modal on mount - show immediately when user visits the site
  useEffect(() => {
    // Always show the modal immediately when user visits the site
    scrollPositionRef.current = window.scrollY;
    setIsOpen(true);
    setShowIntro(true);
  }, []);
  // Lock scroll when open - improved version that preserves position
  useEffect(() => {
    if (isOpen) {
      scrollPositionRef.current = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px'; // Prevent layout shift from scrollbar
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      // Restore scroll position
      window.scrollTo(0, scrollPositionRef.current);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);
  const handleStartSurvey = () => {
    setShowIntro(false);
  };
  const handleAnswer = (scoreValue: number) => {
    const newAnswers = [...answers, scoreValue];
    setAnswers(newAnswers);
    if (currentStep < questions.length - 1) {
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 250);
    } else {
      // Calculate level based on most frequent answer position
      const level = getLevelFromAnswers(newAnswers);
      setCalculatedLevel(level);
      // Show contact form instead of results
      setTimeout(() => {
        setShowContactForm(true);
      }, 250);
    }
  };
  // New level calculation: count frequency of each answer position (1-5)
  // The most frequent position determines the level
  const getLevelFromAnswers = (answersList: number[]): number => {
    const counts: Record<number, number> = {
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    };
    answersList.forEach(score => {
      if (counts[score] !== undefined) {
        counts[score]++;
      }
    });
    // Find the level with the highest count
    let maxCount = 0;
    let resultLevel = 1;
    for (let level = 1; level <= 5; level++) {
      if (counts[level] > maxCount) {
        maxCount = counts[level];
        resultLevel = level;
      }
    }
    return resultLevel;
  };
  const validateForm = (): boolean => {
    const errors = {
      phone: '',
      email: ''
    };
    let isValid = true;
    // Phone validation: must be 10, 12, or 13 characters
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10 && phoneDigits.length !== 12 && phoneDigits.length !== 13) {
      errors.phone = 'Номер телефону має містити 10, 12 або 13 цифр';
      isValid = false;
    }
    // Email validation: must contain @
    if (formData.email && !formData.email.includes('@')) {
      errors.email = 'Email має містити символ @';
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    try {
      // Send data to Google Sheets via Apps Script
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          company: formData.company,
          surveyLevel: calculatedLevel,
          surveyResult: resultsData[calculatedLevel]?.title || '',
          source: 'Survey - Діагностика бізнесу',
          timestamp: new Date().toISOString()
        })
      });
      // Mark survey as completed and show results
      localStorage.setItem('armar_survey_completed', 'true');
      setShowContactForm(false);
      setShowResults(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      // Still show results even if submission fails
      localStorage.setItem('armar_survey_completed', 'true');
      setShowContactForm(false);
      setShowResults(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const closeSurvey = () => {
    setIsOpen(false);
  };
  const handleGoToProgram = () => {
    setIsOpen(false);
    // Small delay to allow modal to close, then scroll
    setTimeout(() => {
      const tariffsSection = document.getElementById('tariffs');
      if (tariffsSection) {
        tariffsSection.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  if (!isOpen) return null;
  const isQuestionStep = !showIntro && !showContactForm && !showResults && currentStep < questions.length;
  const currentQuestion = questions[currentStep];
  const progress = (currentStep + 1) / questions.length * 100;
  const result = resultsData[calculatedLevel];
  // Separate text sections from block sections
  const textSections = result?.sections.filter(s => s.type === 'text') || [];
  const blockSections = result?.sections.filter(s => s.type === 'block') || [];
  return <AnimatePresence data-id="element-454">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={closeSurvey} data-id="element-455">
          <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} className={`relative w-full ${showResults ? 'max-w-4xl' : 'max-w-2xl'} bg-gradient-to-br from-[#0d0a2a] via-[#12103d] to-[#1a1045] rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]`} onClick={e => e.stopPropagation()} data-id="element-456">
            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-white/10 bg-[#08061a]/50 backdrop-blur-md sticky top-0 z-20" data-id="element-457">
              <div className="flex-1" data-id="element-458">
                {showIntro ? <div className="text-purple-400 font-medium text-sm md:text-base flex items-center gap-2" data-id="element-459">
                    <Sparkles className="w-4 h-4" data-id="element-460" />
                    Діагностичний тест
                  </div> : showContactForm ? <div className="text-purple-400 font-medium text-sm md:text-base" data-id="element-461">
                    Останній крок
                  </div> : showResults ? <div className="text-purple-400 font-medium text-sm md:text-base" data-id="element-462">
                    Ваш результат
                  </div> : <div className="flex flex-col gap-2" data-id="element-463">
                    <div className="flex justify-between items-center text-xs md:text-sm text-gray-400" data-id="element-464">
                      <span data-id="element-465">Діагностика бізнесу</span>
                      <span data-id="element-466">
                        Крок {currentStep + 1} з {questions.length}
                      </span>
                    </div>
                    <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-467">
                      <motion.div className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" initial={{
                  width: 0
                }} animate={{
                  width: `${progress}%`
                }} transition={{
                  duration: 0.5
                }} data-id="element-468" />
                    </div>
                  </div>}
              </div>
              <button onClick={closeSurvey} className="ml-4 p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white" data-id="element-469">
                <X className="w-5 h-5 md:w-6 md:h-6" data-id="element-470" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar" data-id="element-471">
              <AnimatePresence mode="wait" data-id="element-472">
                {showIntro ? <motion.div key="intro" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.4
            }} className="flex flex-col items-center text-center space-y-6 py-4 md:py-8" data-id="element-473">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 border border-purple-500/30 flex items-center justify-center" data-id="element-474">
                      <Compass className="w-10 h-10 md:w-12 md:h-12 text-purple-400" data-id="element-475" />
                    </div>

                    <div className="space-y-2" data-id="element-476">
                      <h2 className="text-2xl md:text-4xl font-bold text-white" data-id="element-477">
                        Діагностичний тест
                      </h2>
                      <p className="text-lg md:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 font-medium" data-id="element-478">
                        «На якому рівні спіралі розвитку знаходиться Ваш бізнес»
                      </p>
                    </div>

                    <p className="text-gray-300 text-base md:text-lg max-w-lg leading-relaxed" data-id="element-479">
                      Давайте чесно перевіримо, де Ви зараз і що заважає перейти
                      на наступний рівень.
                    </p>

                    <div className="bg-white/5 border border-white/10 rounded-xl p-5 md:p-6 w-full max-w-lg" data-id="element-480">
                      <p className="text-gray-200 font-medium mb-4 text-left" data-id="element-481">
                        Пройдіть короткий тест – і Ви отримаєте:
                      </p>
                      <ul className="space-y-3 text-left" data-id="element-482">
                        {[{
                    icon: BarChart3,
                    text: 'розуміння свого поточного управлінського рівня'
                  }, {
                    icon: TrendingUp,
                    text: 'зони росту бізнесу'
                  }, {
                    icon: Target,
                    text: 'підказку, який наступний крок дасть найбільший ефект'
                  }].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-483">
                            <div className="p-1.5 rounded-lg bg-purple-500/20 text-purple-400 shrink-0 mt-0.5" data-id="element-484">
                              <item.icon className="w-4 h-4" data-id="element-485" />
                            </div>
                            <span className="text-gray-300 text-sm md:text-base" data-id="element-486">
                              {item.text}
                            </span>
                          </li>)}
                      </ul>
                    </div>

                    <p className="text-gray-400 text-sm md:text-base" data-id="element-487">
                      Займе 3–4 хвилини
                    </p>

                    <Button size="lg" onClick={handleStartSurvey} withArrow className="mt-2" data-id="element-488">
                      Почати
                    </Button>
                  </motion.div> : showContactForm ? <motion.div key="contact-form" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.4
            }} className="space-y-6" data-id="element-489">
                    <div className="text-center space-y-2" data-id="element-490">
                      <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-500/20 to-blue-500/20 border border-green-500/30 flex items-center justify-center mb-4" data-id="element-491">
                        <CheckCircle2 className="w-8 h-8 text-green-400" data-id="element-492" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white" data-id="element-493">
                        Чудово! Тест завершено
                      </h2>
                      <p className="text-gray-300 text-base md:text-lg" data-id="element-494">
                        Залиште свої контактні дані, щоб побачити результати
                      </p>
                    </div>

                    <form onSubmit={handleContactSubmit} className="space-y-4 max-w-md mx-auto" data-id="element-495">
                      <div data-id="element-496">
                        <label className="block text-sm text-gray-400 mb-2" data-id="element-497">
                          Ім'я та прізвище *
                        </label>
                        <input type="text" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-base" placeholder="Введіть ваше ім'я" required disabled={isSubmitting} data-id="element-498" />
                      </div>

                      <div data-id="element-499">
                        <label className="block text-sm text-gray-400 mb-2" data-id="element-500">
                          Номер телефону *
                        </label>
                        <input type="tel" value={formData.phone} onChange={e => {
                    setFormData({
                      ...formData,
                      phone: e.target.value
                    });
                    if (formErrors.phone) {
                      setFormErrors({
                        ...formErrors,
                        phone: ''
                      });
                    }
                  }} className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors text-base ${formErrors.phone ? 'border-red-500' : 'border-white/20 focus:border-purple-500'}`} placeholder="+380" required disabled={isSubmitting} data-id="element-501" />
                        {formErrors.phone && <p className="text-red-400 text-sm mt-1" data-id="element-502">
                            {formErrors.phone}
                          </p>}
                      </div>

                      <div data-id="element-503">
                        <label className="block text-sm text-gray-400 mb-2" data-id="element-504">
                          Email
                        </label>
                        <input type="text" value={formData.email} onChange={e => {
                    setFormData({
                      ...formData,
                      email: e.target.value
                    });
                    if (formErrors.email) {
                      setFormErrors({
                        ...formErrors,
                        email: ''
                      });
                    }
                  }} className={`w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-500 focus:outline-none transition-colors text-base ${formErrors.email ? 'border-red-500' : 'border-white/20 focus:border-purple-500'}`} placeholder="email@example.com" disabled={isSubmitting} data-id="element-505" />
                        {formErrors.email && <p className="text-red-400 text-sm mt-1" data-id="element-506">
                            {formErrors.email}
                          </p>}
                      </div>

                      <div data-id="element-507">
                        <label className="block text-sm text-gray-400 mb-2" data-id="element-508">
                          Назва компанії
                        </label>
                        <input type="text" value={formData.company} onChange={e => setFormData({
                    ...formData,
                    company: e.target.value
                  })} className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-base" placeholder="Назва вашої компанії" disabled={isSubmitting} data-id="element-509" />
                      </div>

                      <Button type="submit" variant="primary" fullWidth className="mt-6" disabled={isSubmitting} data-id="element-510">
                        {isSubmitting ? <span className="flex items-center justify-center gap-2" data-id="element-511">
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-512" />
                            Надсилання...
                          </span> : 'Показати результати'}
                      </Button>
                    </form>
                  </motion.div> : showResults ? <motion.div key="result" initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5
            }} className="space-y-6" data-id="element-513">
                    {/* Result Header */}
                    <div className="text-center space-y-2 mb-6" data-id="element-514">
                      <h2 className="text-2xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-500" data-id="element-515">
                        {result.title}
                      </h2>
                      <p className="text-base md:text-xl text-gray-300 font-medium" data-id="element-516">
                        {result.subtitle}
                      </p>
                    </div>

                    {/* Text Sections - centered titles with tags */}
                    <div className="space-y-6" data-id="element-517">
                      {textSections.map((section, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 15
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.1
                }} className="text-center" data-id="element-518">
                          <h4 className="text-lg md:text-xl font-bold text-white mb-3" data-id="element-519">
                            {section.title}
                          </h4>
                          <div className="flex flex-wrap justify-center gap-2" data-id="element-520">
                            {section.items.map((item, itemIdx) => <span key={itemIdx} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-300 text-xs md:text-sm" data-id="element-521">
                                {item}
                              </span>)}
                          </div>
                        </motion.div>)}
                    </div>

                    {/* Block Sections - Hard Skills & Soft Skills side by side */}
                    {blockSections.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-id="element-522">
                        {blockSections.map((section, idx) => {
                  const Icon = section.icon || CheckCircle2;
                  const isHardSkills = section.title === 'Hard Skills';
                  return <motion.div key={idx} initial={{
                    opacity: 0,
                    y: 20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.3 + idx * 0.1
                  }} className={`bg-gradient-to-br ${isHardSkills ? 'from-blue-900/40 to-blue-800/20 border-blue-500/30' : 'from-purple-900/40 to-purple-800/20 border-purple-500/30'} border rounded-xl p-5 md:p-6 flex-1`} data-id="element-523">
                              <div className="flex items-center gap-3 mb-4" data-id="element-524">
                                <div className={`p-2 rounded-lg ${isHardSkills ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`} data-id="element-525">
                                  <Icon className="w-5 h-5" data-id="element-526" />
                                </div>
                                <h4 className="font-bold text-white text-lg" data-id="element-527">
                                  {section.title}
                                </h4>
                              </div>
                              <ul className="space-y-2.5" data-id="element-528">
                                {section.items.map((item, itemIdx) => <li key={itemIdx} className="flex items-start gap-2" data-id="element-529">
                                    <div className={`w-1.5 h-1.5 rounded-full ${isHardSkills ? 'bg-blue-400' : 'bg-purple-400'} mt-2 shrink-0`} data-id="element-530" />
                                    <span className="text-gray-300 text-sm leading-relaxed" data-id="element-531">
                                      {item}
                                    </span>
                                  </li>)}
                              </ul>
                            </motion.div>;
                })}
                      </div>}

                    {/* CTA Footer - only "Перейти до програми" button */}
                    <div className="flex justify-center pt-6 border-t border-white/10" data-id="element-532">
                      <Button size="lg" onClick={handleGoToProgram} withArrow data-id="element-533">
                        Перейти до програми
                      </Button>
                    </div>
                  </motion.div> : isQuestionStep ? <motion.div key={`question-${currentStep}`} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="space-y-6" data-id="element-534">
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white leading-tight" data-id="element-535">
                      {currentQuestion.text}
                    </h3>

                    <div className="space-y-3" data-id="element-536">
                      {currentQuestion.options.map((option, idx) => <motion.button key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.05
                }} onClick={() => handleAnswer(option.score)} className="w-full text-left p-4 md:p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-purple-600/20 hover:border-purple-500/50 transition-all duration-200 group flex items-start gap-4" data-id="element-537">
                          <div className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-purple-400 group-hover:bg-purple-500/20 transition-colors" data-id="element-538">
                            <div className="w-2.5 h-2.5 rounded-full bg-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-539" />
                          </div>
                          <span className="text-gray-200 group-hover:text-white text-sm md:text-lg leading-relaxed" data-id="element-540">
                            {option.text}
                          </span>
                        </motion.button>)}
                    </div>
                  </motion.div> : null}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}